import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

function PrivacyPolicy() {
  // 取得多國語系翻譯函式
  const { t } = useTranslation();

  // 建立 Intersection Observer 與控制動畫的 hook
  const [ref, inView] = useInView({ triggerOnce: true });
  const controls = useAnimation();

  // 一旦 inView 為 true，啟動動畫
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  // 定義淡入動畫的參數
  const fadeIn = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };

  return (
    <div className="bg-gradient-to-b from-gray-100 to-white pb-20 dark:bg-gradient-to-b dark:from-gray-900 dark:to-gray-800 min-h-screen text-black dark:text-white font-sans">
      <div
        className="w-full h-64 bg-cover bg-center flex items-center justify-center text-white text-3xl font-bold"
        style={{ backgroundImage: `url('/images/myReportsBanner.png')` }}
      >
        {t('privacyPolicy.title')}
        <p>&</p>
        {t('termsOfService.title')}
      </div>


      {/* 隱私權政策 */}
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          className="mb-12"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h2 className="text-4xl font-bold mb-6 mt-8 text-gray-800 dark:text-gray-100">
          {t('privacyPolicy.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8 whitespace-pre-line">
          {t('privacyPolicy.intro')}
          </p>
        </motion.div>
        {/* Section 1 */}
        <motion.div
          ref={ref}
          className="mb-12"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h2 className="text-3xl font-bold mb-4 text-gray-800 dark:text-gray-100">
            {t('privacyPolicy.section1.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line">
            {t('privacyPolicy.section1.content')}
          </p>
        </motion.div>

        {/* Section 2 */}
        <motion.div
          className="mb-12"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h2 className="text-3xl font-bold mb-4 text-gray-800 dark:text-gray-100">
            {t('privacyPolicy.section2.title')}
          </h2>
          {t('privacyPolicy.section2.items', { returnObjects: true }).map((item, idx) => (
            <p
              key={idx}
              className="text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-line"
            >
              {item}
            </p>
          ))}
        </motion.div>

        {/* Section 3 */}
        <motion.div
          className="mb-12"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h2 className="text-3xl font-bold mb-4 text-gray-800 dark:text-gray-100">
            {t('privacyPolicy.section3.title')}
          </h2>
          {t('privacyPolicy.section3.content', { returnObjects: true }).map((paragraph, idx) => (
            <p key={idx} className="text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-line">
              {paragraph}
            </p>
          ))}
        </motion.div>

        {/* Section 4 */}
        <motion.div
          className="mb-12"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h2 className="text-3xl font-bold mb-4 text-gray-800 dark:text-gray-100">
            {t('privacyPolicy.section4.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line">
            {t('privacyPolicy.section4.content')}
          </p>
        </motion.div>

        {/* Section 5 */}
        <motion.div
          className="mb-12"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h2 className="text-3xl font-bold mb-4 text-gray-800 dark:text-gray-100">
            {t('privacyPolicy.section5.title')}
          </h2>
          {t('privacyPolicy.section5.items', { returnObjects: true }).map((item, idx) => (
            <p
              key={idx}
              className="text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-line"
            >
              {item}
            </p>
          ))}
        </motion.div>

        {/* Section 6 */}
        <motion.div
          className="mb-12"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h2 className="text-3xl font-bold mb-4 text-gray-800 dark:text-gray-100">
            {t('privacyPolicy.section6.title')}
          </h2>
          {t('privacyPolicy.section6.content', { returnObjects: true }).map((paragraph, idx) => (
            <p key={idx} className="text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-line">
              {paragraph}
            </p>
          ))}
        </motion.div>

        {/* Section 7 */}
        <motion.div
          className="mb-12"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h2 className="text-3xl font-bold mb-4 text-gray-800 dark:text-gray-100">
            {t('privacyPolicy.section7.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line">
            {t('privacyPolicy.section7.content')}
          </p>
        </motion.div>
      </div>

      {/* 分隔線，區隔隱私權政策與服務條款 */}
      <hr className="border-gray-300 dark:border-gray-700 my-8" />

      {/* 服務條款 */}
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          className="mb-12"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h2 className="text-4xl font-bold mb-6 text-gray-800 dark:text-gray-100">
            {t('termsOfService.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8 whitespace-pre-line">
            {t('termsOfService.intro')}
          </p>
        </motion.div>

        {/* Section 1 */}
        <motion.div
          className="mb-8"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h3 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
            {t('termsOfService.section1.title')}
          </h3>
          {t('termsOfService.section1.content', { returnObjects: true }).map((paragraph, idx) => (
            <p key={idx} className="text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-line">
              {paragraph}
            </p>
          ))}
        </motion.div>

        {/* Section 2 */}
        <motion.div
          className="mb-8"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h3 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
            {t('termsOfService.section2.title')}
          </h3>
          {t('termsOfService.section2.content', { returnObjects: true }).map((paragraph, idx) => (
            <p key={idx} className="text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-line">
              {paragraph}
            </p>
          ))}
        </motion.div>

        {/* Section 3 */}
        <motion.div
          className="mb-8"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h3 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
            {t('termsOfService.section3.title')}
          </h3>
          {t('termsOfService.section3.content', { returnObjects: true }).map((paragraph, idx) => (
            <p key={idx} className="text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-line">
              {paragraph}
            </p>
          ))}
        </motion.div>

        {/* Section 4 */}
        <motion.div
          className="mb-8"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h3 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
            {t('termsOfService.section4.title')}
          </h3>
          {t('termsOfService.section4.content', { returnObjects: true }).map((paragraph, idx) => (
            <p key={idx} className="text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-line">
              {paragraph}
            </p>
          ))}
        </motion.div>

        {/* Section 5 */}
        <motion.div
          className="mb-8"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h3 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
            {t('termsOfService.section5.title')}
          </h3>
          {t('termsOfService.section5.content', { returnObjects: true }).map((paragraph, idx) => (
            <p key={idx} className="text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-line">
              {paragraph}
            </p>
          ))}
        </motion.div>

        {/* Section 6 */}
        <motion.div
          className="mb-8"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h3 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
            {t('termsOfService.section6.title')}
          </h3>
          {t('termsOfService.section6.content', { returnObjects: true }).map((paragraph, idx) => (
            <p key={idx} className="text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-line">
              {paragraph}
            </p>
          ))}
        </motion.div>

        {/* Section 7 */}
        <motion.div
          className="mb-8"
          variants={fadeIn}
          initial="hidden"
          animate={controls}
        >
          <h3 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
            {t('termsOfService.section7.title')}
          </h3>
          {t('termsOfService.section7.content', { returnObjects: true }).map((paragraph, idx) => (
            <p key={idx} className="text-gray-600 dark:text-gray-400 whitespace-pre-line">
              {paragraph}
            </p>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
